import React from "react";
import useCountdown from "./countdown";

const LAUNCH_DATE = new Date("2026-10-18T00:00:00+05:30");

const TimeBox = ({ value, label, compact }) => (
  <div className={`flex flex-col items-center rounded-lg bg-paper shadow-sm ${compact ? "px-2 py-1.5 min-w-[44px]" : "px-3 py-2.5 min-w-[56px] rounded-xl"}`}>
    <span className={`font-bold text-brand tabular-nums ${compact ? "text-base" : "text-xl"}`}>
      {String(value).padStart(2, "0")}
    </span>
    <span className={`uppercase tracking-wide text-muted ${compact ? "text-[9px]" : "text-[10px]"}`}>{label}</span>
  </div>
);

/**
 * A generic smartphone silhouette, drawn in CSS — not a photo of any
 * specific device — showing a live countdown to the app launch.
 *
 * `compact` renders a shorter version (no skeleton content / CTA row)
 * sized to sit beside hero text without forcing extra scroll.
 */
const PhoneMockup = ({ compact = false }) => {
  const time = useCountdown(LAUNCH_DATE);

  if (compact) {
    return (
      <div className="relative mx-auto w-[190px] rounded-[2rem] border-[8px] border-brand bg-brand shadow-2xl">
        <div className="absolute left-1/2 top-0 z-10 h-4 w-20 -translate-x-1/2 rounded-b-xl bg-brand" />
        <div className="relative flex h-[300px] flex-col overflow-hidden rounded-[1.4rem] bg-cloud px-3.5 pb-3.5 pt-6">
          <img src="/assets/logo-icon.png" alt="" className="mx-auto h-8 w-auto" />
          <p className="mt-2 text-center text-[11px] font-semibold leading-tight text-charcoal">
            Nirkana app — almost here
          </p>
          <p className="mt-0.5 text-center text-[10px] text-muted">Oct 18, 2026</p>

          <div className="mt-3 grid grid-cols-4 gap-1.5">
            <TimeBox value={time.days} label="D" compact />
            <TimeBox value={time.hours} label="H" compact />
            <TimeBox value={time.minutes} label="M" compact />
            <TimeBox value={time.seconds} label="S" compact />
          </div>

          <p className="mt-auto pt-3 text-center text-[10px] text-muted">
            Development in progress
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-[260px] rounded-[2.75rem] border-[10px] border-brand bg-brand shadow-2xl">
      <div className="absolute left-1/2 top-0 z-10 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-brand" />
      <div className="relative flex h-[520px] flex-col overflow-hidden rounded-[2.1rem] bg-cloud px-5 pb-6 pt-10">
        <img src="/assets/logo-icon.png" alt="" className="mx-auto h-12 w-auto" />
        <p className="mt-3 text-center text-sm font-semibold text-charcoal">
          The Nirkana app is almost here
        </p>
        <p className="mt-1 text-center text-xs text-muted">
          Launching October 18, 2026
        </p>

        <div className="mt-5 grid grid-cols-4 gap-2">
          <TimeBox value={time.days} label="Days" />
          <TimeBox value={time.hours} label="Hrs" />
          <TimeBox value={time.minutes} label="Min" />
          <TimeBox value={time.seconds} label="Sec" />
        </div>

        <div className="mt-6 space-y-2.5 rounded-xl border border-line bg-paper p-3.5">
          <div className="h-2.5 w-3/5 rounded-full bg-line" />
          <div className="h-2.5 w-full rounded-full bg-line" />
          <div className="h-2.5 w-4/5 rounded-full bg-line" />
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl bg-action px-4 py-3">
          <span className="text-xs font-medium text-paper">Book a birthday</span>
          <span className="text-xs font-medium text-paper">→</span>
        </div>

        <div className="mt-auto pt-4 text-center text-[11px] text-muted">
          Development in progress
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;
